import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui';
import { cn } from '@/lib/utils';

import { SearchInput } from './trace-viewer.search-input';
import { SpanDetails } from './trace-viewer.span-details';
import { SpanNode } from './trace-viewer.span-node';
import { traceViewerStore } from './trace-viewer.store';

import type { TraceSpan } from './trace-viewer.types';

interface TraceViewerLayoutProps {
  traceTree: TraceSpan | null;
  isLoading?: boolean;
  onRefresh?: () => void;
}

export const TraceViewerLayout = observer(
  ({ traceTree, isLoading = false, onRefresh }: TraceViewerLayoutProps) => {
    const store = traceViewerStore;
    useEffect(() => {
      store.setRootSpan(traceTree);
      return () => {
        store.reset();
      };
    }, [traceTree, store]);

    if (isLoading) {
      return (
        <YStack className="h-full items-center justify-center gap-2">
          <Icons.loader className="size-5 animate-spin text-foreground-subtle" />
          <span className="text-sm text-foreground-subtle">
            Loading trace...
          </span>
        </YStack>
      );
    }

    if (!traceTree) {
      return (
        <YStack className="h-full items-center justify-center gap-2">
          <span className="text-sm text-foreground-subtle">
            No trace data available for this message
          </span>
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="text-sm text-foreground-subtle underline hover:text-foreground cursor-pointer"
            >
              Click to refresh
            </button>
          )}
        </YStack>
      );
    }

    return (
      <XStack className="h-full overflow-hidden gap-2">
        <YStack
          className={cn(
            'flex-1 overflow-hidden border-r border-border',
            store.selectedSpan ? 'max-w-[60%]' : 'max-w-full'
          )}
        >
          <SearchInput />
          <YStack className="flex-1 overflow-y-auto py-2 px-1">
            <SpanNode
              span={traceTree}
              depth={0}
              traceStartTime={traceTree.startTime}
              traceDuration={traceTree.duration}
            />
          </YStack>
        </YStack>

        {store.selectedSpan && (
          <YStack className="w-[40%] overflow-hidden bg-background rounded-md">
            <SpanDetails span={store.selectedSpan} />
          </YStack>
        )}
      </XStack>
    );
  }
);
